import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  archiveGuide, createGuide, generateVoicePreview, getGuide, listDomains, listVoices,
  previewVoice, publishGuide, setGuideEnabled, unpublishGuide, updateGuide,
} from "../services/guides";
import {
  AURA_GUIDE_ID, DIRECTNESS, GUIDE_CATEGORIES, PAUSE_STYLE, SENTENCE_LENGTH, STRUCTURE, WARMTH,
  emptyProfile,
} from "../types/guide";
import type { CharacterProfile, DomainCatalog, VoiceCatalog } from "../types/guide";
import { PromptPreview } from "../components/bench/PromptPreview";
import { TextBench } from "../components/bench/TextBench";
import { VoiceBench } from "../components/bench/VoiceBench";
import { GuideAvatarPicker } from "../components/GuideAvatarPicker";
import { GuideSkillsPicker } from "../components/skills/GuideSkillsPicker";
import { GuideInsights } from "../components/analytics/GuideInsights";
import { CopilotPanel } from "../components/copilot/CopilotPanel";
import { VersionHistory } from "../components/history/VersionHistory";
import { guideGender, liveVoiceError, liveVoiceOptions } from "../components/liveVoice";

type Tab = "prompt" | "text" | "voice" | "copilot" | "history" | "insights";

const TABS: Tab[] = ["prompt", "text", "voice", "copilot", "history", "insights"];

export function GuideEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [profile, setProfile] = useState<CharacterProfile>(emptyProfile());
  const [savedId, setSavedId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<VoiceCatalog>({});
  const [servableProviders, setServableProviders] = useState<string[]>([]);
  const [liveVoices, setLiveVoices] = useState<string[]>([]);
  const [playingLive, setPlayingLive] = useState(false);
  const [domainCatalog, setDomainCatalog] = useState<DomainCatalog | null>(null);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);
  // Deep-linkable tab (e.g. ?tab=insights from the Dashboard's top-companions
  // table). Insights only exists for saved guides.
  const [tab, setTab] = useState<Tab>(() => {
    const wanted = searchParams.get("tab") as Tab | null;
    const savedOnly = wanted === "insights" || wanted === "history";
    if (wanted && TABS.includes(wanted) && !(savedOnly && !id)) return wanted;
    return "prompt";
  });
  const [previewText, setPreviewText] = useState("Hi, I'm your companion. I'm really glad you're here.");
  const [generating, setGenerating] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(false);
  const [lastGeneratedText, setLastGeneratedText] = useState("");
  const [lastGeneratedTts, setLastGeneratedTts] = useState<{
    provider: string;
    voiceId: string;
    speed: number;
  } | null>(null);

  const setStatusMsg = useCallback((text: string, isError = false) => {
    setStatus(text);
    setStatusError(isError);
  }, []);

  const handleSkillsError = useCallback((m: string) => {
    setStatusMsg(m, true);
  }, [setStatusMsg]);

  // Load voice catalog once.
  useEffect(() => {
    listVoices()
      .then((r) => {
        setCatalog(r.providers);
        setServableProviders(r.servableProviders || []);
        setLiveVoices(r.liveVoices || []);
      })
      .catch((e) => setStatusMsg(`Voice catalog failed: ${e.message}`, true));
  }, []);

  // Routing vocabulary (domains + who owns each now) once.
  useEffect(() => {
    listDomains()
      .then(setDomainCatalog)
      .catch((e) => setStatusMsg(`Domain list failed: ${e.message}`, true));
  }, [setStatusMsg]);

  // Load (or reset) the guide when the route id changes.
  useEffect(() => {
    if (!id) {
      setProfile(emptyProfile());
      setSavedId(null);
      setTab((t) => (t === "insights" || t === "history" ? "prompt" : t));
      setPreviewText("Hi, I'm your companion. I'm really glad you're here.");
      setLastGeneratedText("");
      setLastGeneratedTts(null);
      setStatusMsg("new companion (unsaved)");
      return;
    }
    getGuide(id)
      .then((g) => {
        setProfile(g);
        setSavedId(g.id);
        setStatusMsg(`${g.id} — status: ${g.status}${g.enabled === false ? " · DISABLED" : ""}`);
        const defaultText = g.greetingVariants[0] || "Hi, I'm your companion. I'm really glad you're here.";
        setPreviewText(defaultText);
        if (g.voicePreviewUrl) {
          setLastGeneratedText(defaultText);
          setLastGeneratedTts({
            provider: g.tts.provider,
            voiceId: g.tts.voiceId,
            speed: g.tts.speed,
          });
        } else {
          setLastGeneratedText("");
          setLastGeneratedTts(null);
        }
      })
      .catch((e) => setStatusMsg(`Failed to load ${id}: ${e.message}`, true));
  }, [id]);

  const patch = useCallback((partial: Partial<CharacterProfile>) => {
    setProfile((p) => ({ ...p, ...partial }));
  }, []);

  const toggleDomain = useCallback((domain: string) => {
    setProfile((p) => ({
      ...p,
      domains: p.domains.includes(domain) ? p.domains.filter((d) => d !== domain) : [...p.domains, domain],
    }));
  }, []);

  const isAura = profile.id.trim().toLowerCase() === AURA_GUIDE_ID || profile.isDefault;
  const knownDomains = useMemo(
    () => new Set((domainCatalog?.domains ?? []).map((d) => d.id)),
    [domainCatalog],
  );
  // Selected domains another offered guide owns: a published, eligible save is refused.
  const domainClashes = useMemo(
    () =>
      (domainCatalog?.domains ?? [])
        .filter((d) => profile.domains.includes(d.id))
        .map((d) => ({ ...d, others: d.owners.filter((o) => o !== savedId) }))
        .filter((d) => d.others.length > 0),
    [domainCatalog, profile.domains, savedId],
  );

  const providers = useMemo(() => Object.keys(catalog), [catalog]);
  const voices = catalog[profile.tts.provider] || [];
  const voiceKnown = voices.some((v) => v.voiceId === profile.tts.voiceId);
  // Preview-only providers (no realtime streaming adapter, e.g. gemini) can be
  // auditioned but a guide saved with one cannot run live voice sessions.
  const providerServable =
    servableProviders.length === 0 || servableProviders.includes(profile.tts.provider);

  // Gemini Live voice: required to publish, the guide's gender first.
  const liveVoice = profile.tts.liveVoiceId || "";
  const liveOptions = useMemo(() => {
    const gender = guideGender(Object.values(catalog).flat(), [profile.tts.voiceId, profile.tts.liveVoiceId]);
    return liveVoiceOptions(catalog["gemini"] || [], liveVoices, gender);
  }, [catalog, liveVoices, profile.tts.voiceId, profile.tts.liveVoiceId]);
  const liveVoiceKnown = !liveVoice || liveOptions.some((v) => v.voiceId === liveVoice);
  const liveError = liveVoiceError(liveVoice, liveVoices);

  const isOutdated = useMemo(() => {
    if (!profile.voicePreviewUrl) return true;
    if (!lastGeneratedTts) return false;
    return (
      previewText !== lastGeneratedText ||
      profile.tts.provider !== lastGeneratedTts.provider ||
      profile.tts.voiceId !== lastGeneratedTts.voiceId ||
      profile.tts.speed !== lastGeneratedTts.speed
    );
  }, [previewText, lastGeneratedText, profile.tts, lastGeneratedTts, profile.voicePreviewUrl]);

  async function save() {
    const cleanId = profile.id.trim().toLowerCase();
    if (!cleanId) return setStatusMsg("Companion id is required", true);
    const toSave = { ...profile, id: cleanId };
    try {
      const saved = savedId ? await updateGuide(toSave) : await createGuide(toSave);
      setProfile(saved);
      setStatusMsg(`Saved ${saved.id} ✓`);
      if (!savedId) navigate(`/guides/${saved.id}`, { replace: true });
      else setSavedId(saved.id);
    } catch (e) {
      setStatusMsg(`Save failed: ${e instanceof Error ? e.message : e}`, true);
    }
  }

  async function lifecycle(action: "publish" | "unpublish" | "archive" | "enable" | "disable") {
    if (!savedId) return setStatusMsg("Save the companion first", true);
    try {
      let updated: CharacterProfile;
      const versionBefore = profile.version;
      if (action === "publish") updated = await publishGuide(savedId);
      else if (action === "unpublish") updated = await unpublishGuide(savedId);
      else if (action === "enable") updated = await setGuideEnabled(savedId, true);
      else if (action === "disable") updated = await setGuideEnabled(savedId, false);
      else {
        if (!confirm(`Archive "${savedId}"? It will be disabled and hidden from the catalog.`)) return;
        updated = await archiveGuide(savedId);
      }
      setProfile(updated);
      // Publishing an unchanged live guide dedupes server-side (no new
      // version) — don't claim a publish happened.
      if (action === "publish" && updated.version === versionBefore && profile.status === "published") {
        setStatusMsg(`already published — no changes since v${updated.version}`);
      } else {
        setStatusMsg(`${action} ✓`);
      }
    } catch (e) {
      setStatusMsg(`${action} failed: ${e instanceof Error ? e.message : e}`, true);
    }
  }

  function duplicate() {
    const copy: CharacterProfile = {
      ...structuredClone(profile),
      id: `${profile.id}-copy`,
      name: `${profile.name} Copy`,
      status: "draft",
      derivedFromId: profile.id || null,
    };
    setProfile(copy);
    setSavedId(null);
    navigate("/guides/new", { replace: true });
    setStatusMsg("Duplicated — adjust the id and save");
  }

  async function doGenerateVoicePreview() {
    if (!savedId) return;
    setGenerating(true);
    try {
      const updated = await generateVoicePreview(savedId, previewText);
      setProfile(updated);
      setLastGeneratedText(previewText);
      setLastGeneratedTts({
        provider: updated.tts.provider,
        voiceId: updated.tts.voiceId,
        speed: updated.tts.speed,
      });
      setStatusMsg("Voice preview generated and saved");
    } catch (e) {
      setStatusMsg(`Generate failed: ${e instanceof Error ? e.message : e}`, true);
    } finally {
      setGenerating(false);
    }
  }

  async function doPlayLiveVoice() {
    if (!liveVoice) return;
    setPlayingLive(true);
    try {
      const url = URL.createObjectURL(await previewVoice("gemini", liveVoice, previewText));
      const audio = new Audio(url);
      audio.onended = audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPlayingLive(false);
      };
      await audio.play();
    } catch (e) {
      setStatusMsg(`Live voice preview failed: ${(e as Error).message}`, true);
      setPlayingLive(false);
    }
  }

  function doPlaySavedPreview() {
    if (!profile.voicePreviewUrl) return;
    setPlayingPreview(true);
    const audio = new Audio(profile.voicePreviewUrl);
    audio.onended = audio.onerror = () => setPlayingPreview(false);
    audio.play();
  }

  return (
    <div className="editor-page">
      <div className="editor-main">
        <a className="back-link" onClick={() => navigate("/companions")} style={{ cursor: "pointer" }}>← All companions</a>
        <div className="toolbar">
          <button className="btn btn-primary" onClick={save}>Save</button>
          <button className="btn btn-good" onClick={() => lifecycle("publish")} disabled={!savedId}>Publish</button>
          <button className="btn" onClick={() => lifecycle("unpublish")} disabled={!savedId}>Unpublish</button>
          <button
            className="btn"
            onClick={() => lifecycle(profile.enabled === false ? "enable" : "disable")}
            disabled={!savedId}
          >
            {profile.enabled === false ? "Enable" : "Disable"}
          </button>
          <button className="btn" onClick={duplicate} disabled={!profile.id}>Duplicate</button>
          <div className="spacer" />
          <button className="btn btn-danger" onClick={() => lifecycle("archive")} disabled={!savedId}>Archive</button>
        </div>
        <div className={`status-line${statusError ? " error" : ""}`}>{status}</div>

        <fieldset>
          <legend>Identity &amp; catalog</legend>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "20px", alignItems: "start" }}>
            {/* Column 1 (140px): Avatar & Button */}
            <div>
              <GuideAvatarPicker
                profile={profile}
                savedId={savedId}
                onUpdateProfile={patch}
                setStatusMsg={setStatusMsg}
              />
            </div>

            {/* Column 2 (Rest): Form fields */}
            <div>
              <div className="row-3">
                <div>
                  <label>Id (slug)</label>
                  <input type="text" value={profile.id} disabled={!!savedId}
                    placeholder="nova" onChange={(e) => patch({ id: e.target.value })} />
                </div>
                <div>
                  <label>Name</label>
                  <input type="text" value={profile.name} placeholder="Nova"
                    onChange={(e) => patch({ name: e.target.value })} />
                </div>
                <div>
                  <label>Role</label>
                  <input type="text" value={profile.role} placeholder="Nutrition"
                    onChange={(e) => patch({ role: e.target.value })} />
                </div>
              </div>
              <div className="row-3">
                <div>
                  <label>Category</label>
                  <select value={profile.category} onChange={(e) => patch({ category: e.target.value as CharacterProfile["category"] })}>
                    {GUIDE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label>Sort order</label>
                  <input type="number" value={profile.sortOrder}
                    onChange={(e) => patch({ sortOrder: parseInt(e.target.value || "0", 10) })} />
                </div>
                <div>
                  <label>Accent color</label>
                  <input type="text" value={profile.accentColor || ""} placeholder="#7c6cf5"
                    onChange={(e) => patch({ accentColor: e.target.value || null })} />
                </div>
              </div>
              <label>Tagline</label>
              <input type="text" value={profile.tagline} onChange={(e) => patch({ tagline: e.target.value })} />
              <label>Description</label>
              <textarea rows={2} value={profile.description} onChange={(e) => patch({ description: e.target.value })} />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Routing (hand-over)</legend>
          <label>Domains — the subjects this guide owns</label>
          <div className="chip-row">
            {(domainCatalog?.domains ?? []).map((d) => {
              const on = profile.domains.includes(d.id);
              const others = d.owners.filter((o) => o !== savedId);
              return (
                <label key={d.id} className={`domain-chip${on ? " on" : ""}${on && others.length ? " clash" : ""}`}
                  title={others.length ? `Owned now by ${others.join(", ")}` : d.id}>
                  <input type="checkbox" checked={on} onChange={() => toggleDomain(d.id)} />
                  {d.label}
                  {others.length > 0 && <span className="domain-owner">· {others.join(", ")}</span>}
                </label>
              );
            })}
            {domainCatalog && profile.domains.filter((d) => !knownDomains.has(d)).map((d) => (
              <label key={d} className="domain-chip on unknown" title="Not in the vocabulary: remove it to save">
                <input type="checkbox" checked onChange={() => toggleDomain(d)} />
                {d} (unknown)
              </label>
            ))}
          </div>
          {profile.domains.length === 0 && (
            <div className="muted tt-small">
              None selected: saving uses the category default
              ({(domainCatalog?.categoryDefaults[profile.category] ?? []).join(", ") || "none for custom"}).
            </div>
          )}
          {domainClashes.length > 0 && profile.handoverEligible && !isAura && (
            <div className="callout">
              ⚠ {domainClashes.map((d) => `${d.label} is owned by ${d.others.join(", ")}`).join("; ")}.
              A published guide offered for hand-over can't share a domain — pick others or turn
              hand-over off.
            </div>
          )}
          <label className="tt-toggle">
            <input type="checkbox" checked={profile.handoverEligible && !isAura} disabled={isAura}
              onChange={(e) => patch({ handoverEligible: e.target.checked })} />
            Offer for hand-over — Aura may propose this guide for its domains (published guides only)
          </label>
          {isAura && <div className="muted tt-small">Aura is Main and is never offered for a hand-over.</div>}
        </fieldset>

        <fieldset>
          <legend>UI Display (mobile app)</legend>
          <div className="row-3">
            <div>
              <label>Speciality</label>
              <input type="text" value={profile.speciality} placeholder="Anxiety & stress"
                onChange={(e) => patch({ speciality: e.target.value })} />
            </div>
            <div>
              <label>Profession</label>
              <input type="text" value={profile.profession} placeholder="Clinical Psychology · 8 yrs"
                onChange={(e) => patch({ profession: e.target.value })} />
            </div>
            <div>
              <label>Voice style</label>
              <input type="text" value={profile.voiceStyle} placeholder="Calm, articulate"
                onChange={(e) => patch({ voiceStyle: e.target.value })} />
            </div>
          </div>
          <div className="row-3">
            <div>
              <label>Years of experience</label>
              <input type="number" min={0} value={profile.yearsOfExperience}
                onChange={(e) => patch({ yearsOfExperience: parseInt(e.target.value || "0", 10) })} />
            </div>
            <div>
              <label>Rating (0–5)</label>
              <input type="number" min={0} max={5} step={0.1} value={profile.rating}
                onChange={(e) => patch({ rating: parseFloat(e.target.value || "0") })} />
            </div>
            <div>
              <label>Age</label>
              <input type="number" min={0} value={profile.age ?? ""}
                onChange={(e) => patch({ age: e.target.value ? parseInt(e.target.value) : null })} />
            </div>
          </div>
          <label>Experience in (one per line)</label>
          <textarea rows={3} value={profile.experienceIn.join("\n")}
            onChange={(e) => patch({ experienceIn: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
          <label>How it works (one per line)</label>
          <textarea rows={3} value={profile.howItWorks.join("\n")}
            onChange={(e) => patch({ howItWorks: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
          <label>Best for (one per line)</label>
          <textarea rows={3} value={profile.bestFor.join("\n")}
            onChange={(e) => patch({ bestFor: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
        </fieldset>

        <fieldset>
          <legend>Prompt — persona &amp; knowledge</legend>
          <label>Persona overlay (who this companion is)</label>
          <textarea rows={4} value={profile.personaOverlay} onChange={(e) => patch({ personaOverlay: e.target.value })} />
          <label>Knowledge pack (curated expertise injected into the prompt)</label>
          <textarea rows={5} value={profile.knowledgePack} onChange={(e) => patch({ knowledgePack: e.target.value })} />
          <label>Boundaries (additive guardrails — can narrow scope, never widen it)</label>
          {profile.boundaries.map((b, i) => (
            <div className="boundary" key={i}>
              <input type="text" value={b} onChange={(e) => {
                const next = [...profile.boundaries];
                next[i] = e.target.value;
                patch({ boundaries: next });
              }} />
              <button className="btn btn-sm" onClick={() => patch({ boundaries: profile.boundaries.filter((_, j) => j !== i) })}>✕</button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={() => patch({ boundaries: [...profile.boundaries, ""] })}>+ boundary</button>
          <label style={{ marginTop: 10 }}>Greeting variants (one per line)</label>
          <textarea rows={3} value={profile.greetingVariants.join("\n")}
            onChange={(e) => patch({ greetingVariants: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} />
        </fieldset>

        <fieldset>
          <legend>Personality</legend>
          <div className="row-4">
            <div>
              <label>Warmth</label>
              <select value={profile.responseStyle.warmth}
                onChange={(e) => patch({ responseStyle: { ...profile.responseStyle, warmth: e.target.value as CharacterProfile["responseStyle"]["warmth"] } })}>
                {WARMTH.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label>Directness</label>
              <select value={profile.responseStyle.directness}
                onChange={(e) => patch({ responseStyle: { ...profile.responseStyle, directness: e.target.value as CharacterProfile["responseStyle"]["directness"] } })}>
                {DIRECTNESS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label>Structure</label>
              <select value={profile.responseStyle.structure}
                onChange={(e) => patch({ responseStyle: { ...profile.responseStyle, structure: e.target.value as CharacterProfile["responseStyle"]["structure"] } })}>
                {STRUCTURE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Max sentences</label>
              <input type="number" min={1} max={12} value={profile.responseStyle.maxSentences}
                onChange={(e) => patch({ responseStyle: { ...profile.responseStyle, maxSentences: parseInt(e.target.value || "4", 10) } })} />
            </div>
          </div>
          <div className="row-4">
            <div>
              <label>Sentence length</label>
              <select value={profile.speechStyle.sentenceLength}
                onChange={(e) => patch({ speechStyle: { ...profile.speechStyle, sentenceLength: e.target.value as CharacterProfile["speechStyle"]["sentenceLength"] } })}>
                {SENTENCE_LENGTH.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Pause style</label>
              <select value={profile.speechStyle.pauseStyle}
                onChange={(e) => patch({ speechStyle: { ...profile.speechStyle, pauseStyle: e.target.value as CharacterProfile["speechStyle"]["pauseStyle"] } })}>
                {PAUSE_STYLE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Allow ellipsis</label>
              <select value={String(profile.speechStyle.allowEllipsis)}
                onChange={(e) => patch({ speechStyle: { ...profile.speechStyle, allowEllipsis: e.target.value === "true" } })}>
                <option value="true">yes</option>
                <option value="false">no</option>
              </select>
            </div>
            <div>
              <label>Allow fillers</label>
              <select value={String(profile.speechStyle.allowFillers)}
                onChange={(e) => patch({ speechStyle: { ...profile.speechStyle, allowFillers: e.target.value === "true" } })}>
                <option value="false">no</option>
                <option value="true">yes</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Voice</legend>
          <div className="row-3">
            <div>
              <label>Provider</label>
              <select value={profile.tts.provider}
                onChange={(e) => patch({ tts: { ...profile.tts, provider: e.target.value as CharacterProfile["tts"]["provider"] } })}>
                {(providers.length ? providers : [profile.tts.provider]).map((p) => (
                  <option key={p} value={p}>
                    {servableProviders.length && !servableProviders.includes(p) ? `${p} (preview only)` : p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Voice</label>
              <select value={profile.tts.voiceId}
                onChange={(e) => patch({ tts: { ...profile.tts, voiceId: e.target.value } })}>
                {voices.map((v) => (
                  <option key={v.voiceId} value={v.voiceId}>
                    {[v.name, [v.gender, v.style].filter(Boolean).join(", ")].filter(Boolean).join(" — ")}
                  </option>
                ))}
                {!voiceKnown && <option value={profile.tts.voiceId}>{profile.tts.voiceId} (not in catalog)</option>}
              </select>
            </div>
            <div>
              <label>Speed</label>
              <input type="number" min={0.5} max={2} step={0.05} value={profile.tts.speed}
                onChange={(e) => patch({ tts: { ...profile.tts, speed: parseFloat(e.target.value || "1") } })} />
            </div>
          </div>

          <label htmlFor="live-voice" style={{ marginTop: 10 }}>Gemini Live voice (required to publish)</label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <select id="live-voice" value={liveVoice} style={{ flex: 1 }} required aria-invalid={!!liveError}
              aria-describedby={liveError ? "live-voice-error" : undefined}
              onChange={(e) => patch({ tts: { ...profile.tts, liveVoiceId: e.target.value || null } })}>
              {!liveVoice && <option value="" disabled>Pick a voice…</option>}
              {liveOptions.map((v) => (
                <option key={v.voiceId} value={v.voiceId}>
                  {[v.name, [v.gender, v.style].filter(Boolean).join(", ")].filter(Boolean).join(" — ")}
                </option>
              ))}
              {!liveVoiceKnown && <option value={liveVoice}>{liveVoice} (not allowed on Live)</option>}
            </select>
            <button className="btn" onClick={doPlayLiveVoice} disabled={playingLive || !liveVoice}>
              {playingLive ? "…playing" : "▶ Play"}
            </button>
          </div>
          {liveError && (
            <div id="live-voice-error" className="error-text" style={{ marginTop: 4 }}>{liveError}</div>
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            The voice on Gemini Live calls. Play reads the preview text below with Gemini TTS, close to
            a Live call. A change reaches new calls within about 5 minutes.
          </div>

          <label style={{ marginTop: 10 }}>Preview text</label>
          <input type="text" maxLength={300} value={previewText} onChange={(e) => setPreviewText(e.target.value)} />

          <label style={{ marginTop: 10 }}>Voice Preview URL</label>
          <input
            type="text"
            value={profile.voicePreviewUrl || ""}
            placeholder="https://..."
            onChange={(e) => patch({ voicePreviewUrl: e.target.value })}
          />

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            {profile.voicePreviewUrl && (
              <button className="btn btn-good" onClick={doPlaySavedPreview} disabled={playingPreview} style={{ flex: 1 }}>
                {playingPreview ? "…playing" : "▶ Play Preview"}
              </button>
            )}
            {isOutdated && (
              <button className="btn btn-primary" onClick={doGenerateVoicePreview} disabled={generating || !savedId} style={{ flex: 1 }}>
                {generating ? "…generating" : profile.voicePreviewUrl ? "🔄 Regenerate Preview" : "🔊 Generate Preview"}
              </button>
            )}
          </div>
          {!savedId && !profile.voicePreviewUrl && (
            <div className="callout">Save the guide first to generate a voice preview.</div>
          )}
          {!providerServable && (
            <div className="callout">
              ⚠ {profile.tts.provider} voices are <strong>preview-only</strong> — the live voice
              pipeline has no realtime streaming for this provider. Pick a{" "}
              {servableProviders.join(" or ")} voice before publishing or voice-testing.
            </div>
          )}
        </fieldset>

        <fieldset>
          <legend>Skills (knowledge bases)</legend>
          <GuideSkillsPicker guideId={savedId} onError={handleSkillsError} />
        </fieldset>
      </div>

      <div className="editor-side">
        <div className="tabs">
          <button className={tab === "prompt" ? "active" : ""} onClick={() => setTab("prompt")}>Prompt preview</button>
          <button className={tab === "text" ? "active" : ""} onClick={() => setTab("text")}>Chat</button>
          <button className={tab === "voice" ? "active" : ""} onClick={() => setTab("voice")}>Voice</button>
          <button className={tab === "copilot" ? "active" : ""} onClick={() => setTab("copilot")}>Copilot</button>
          {savedId && (
            <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
              History
            </button>
          )}
          {savedId && (
            <button className={tab === "insights" ? "active" : ""} onClick={() => setTab("insights")}>
              Insights
            </button>
          )}
        </div>
        <div className="side-body">
          {tab === "prompt" && <PromptPreview profile={profile} />}
          {tab === "text" && <TextBench profile={profile} />}
          {tab === "voice" && <VoiceBench savedId={savedId} />}
          {/* Kept mounted so the conversation survives tab switches */}
          <div style={{ display: tab === "copilot" ? undefined : "none" }}>
            <CopilotPanel
              context={{ kind: "guide", id: savedId, draftProfile: profile }}
              current={profile as unknown as Record<string, unknown>}
              onApplyPatch={(p) => {
                patch(p as Partial<CharacterProfile>);
                setStatusMsg("Copilot patch applied — review and Save");
              }}
            />
          </div>
          {tab === "history" && savedId && (
            <VersionHistory
              guideId={savedId}
              currentVersion={profile.version}
              current={profile}
              onRestored={(restored, message) => {
                setProfile(restored);
                setStatusMsg(message);
              }}
            />
          )}
          {tab === "insights" && savedId && <GuideInsights guideId={savedId} />}
        </div>
      </div>
    </div>
  );
}
